/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import AdobeExperienceCloud from '@react/react-spectrum/Icon/AdobeExperienceCloud';
import Button from '@react/react-spectrum/Button';
import classNames from 'classnames';
import ColorIcon from './SolutionSwitcher/ColorIcons/ColorIcon';
import Label from '@react/react-spectrum/Label';
import PropTypes from 'prop-types';
import React from 'react';
import ShellWorkspace from './ShellWorkspace';
import './ShellHeader.scss';

/**
 * Experience Cloud Shell Header component. Container for all shell sub-components.
 * @param {Object} <ObjectPattern> Props from parent component.
 * @returns {ReactElement} Markup for ShellHeader component.
 */
function ShellHeader({
  actions,
  customEnvLabel,
  envLabel,
  modal,
  onSolutionClick,
  onWorkspaceChange,
  selectedWorkspaceId,
  size,
  solutionIcon,
  solutionShortTitle,
  solutionTitle,
  viewportTakeover,
  workspaces,
  ...otherProps
}) {
  return (
    <header {...otherProps} aria-hidden={modal || viewportTakeover} className="spectrum-Shell spectrum--darkest">
      <div className={classNames('spectrum-Underlay', {'is-open': modal})} style={{position: 'absolute'}}></div>
      <div className="spectrum-Shell-leftContainer">
        <Button
          aria-labelledby="spectrum-Shell-logoLabel"
          className="spectrum-Shell-logoIcon"
          onClick={onSolutionClick}
          onKeyUp={onSolutionClick}
          variant="tool"
        >
          {solutionIcon.type !== 'span' ? solutionIcon : <ColorIcon iconName="AdobeExperienceCloud" iconSize="M" />}
          {size !== 'S' && (
            <h1 className="spectrum-Shell-logoLabel" id="spectrum-Shell-logoLabel">
              {size === 'L' ? solutionTitle || 'Adobe Experience Cloud' : solutionShortTitle || 'AEC'}
            </h1>
          )}
        </Button>
        {envLabel && (
          <Label
            className={classNames('spectrum-Shell-envLabel', customEnvLabel && 'smallMargin')}
            variant="orange"
          >
            {envLabel}
          </Label>
        )}
        {customEnvLabel && (
          <Label className="spectrum-Shell-envLabel" variant="green">
            {customEnvLabel}
          </Label>
        )}
        <ShellWorkspace onChange={onWorkspaceChange} selected={selectedWorkspaceId} workspaces={workspaces} />
      </div>
      <div className="spectrum-Shell-rightContainer">{actions}</div>
    </header>
  );
}

ShellHeader.defaultProps = {
  actions: [],
  modal: false,
  onSolutionClick: () => {},
  onWorkspaceChange: () => {},
  selectedWorkspaceId: '',
  size: 'L',
  solutionIcon: <AdobeExperienceCloud />,
  solutionShortTitle: 'Experience Cloud',
  solutionTitle: 'Adobe Experience Cloud',
  viewportTakeover: false,
  workspaces: []
};

ShellHeader.propTypes = {
  /** Actions for the right side of the Shell */
  actions: PropTypes.arrayOf(PropTypes.node),
  /** Whether a modal is showing or not */
  modal: PropTypes.bool,
  /** Is triggered when the solution name is clicked */
  onSolutionClick: PropTypes.func,
  /** Is triggered when the workspace is changed */
  onWorkspaceChange: PropTypes.func,
  /** Is triggered when the solution name is clicked */
  selectedWorkspaceId: PropTypes.string,
  /** Size variant: S/M/L */
  size: PropTypes.oneOf(['S', 'M', 'L']),
  /** The solution icon or image */
  solutionIcon: PropTypes.node,
  /** The short name of the solution */
  solutionShortTitle: PropTypes.string,
  /** The full name of the solution */
  solutionTitle: PropTypes.string,
  /** Whether there is viewportTakeover or not */
  viewportTakeover: PropTypes.bool,
  /** List of workspaces to show in the left side of the shell */
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      /** Size variant: S/M/L */
      name: PropTypes.string,
      /** URL for workspace item */
      url: PropTypes.string
    })
  )
};

export default React.memo(ShellHeader);
